import { FC, useEffect, useRef, useState } from 'react';
import { DynamicBookResponse, Pax } from 'ts/dynamic/interfaces/DynamicBookResponse';
import DynamicBookForm from './DynamicBookForm';
import DynamicBookSummary from './DetailsBookSummary';
import ExpiredToken from 'components/shared/ExpiredToken';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { saveScreenshot } from 'utils/utils';
import { updatePaymentBlock } from 'services/dynamic';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import { b2b } from 'constants/generic';

const BookDynamicPage: FC = () => {
  const [data, setData] = useState<DynamicBookResponse>();
  const bookRef = useRef<HTMLDivElement>(null);
  const [couponAmount, setCouponAmount] = useState('');
  const [addPrice, setAddPrice] = useState(0);
  const [urlParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const { pathname } = useLocation();
  const [paxes, setPaxes] = useState<Pax[]>();
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => {});
  const matrixConfig = urlParams.get('matrixConfig')?.toLowerCase() || '';
  const isEvents = urlParams.get('searchOption') === 'Events';
  const moduleConfig = isEvents ? 'dynamic_am_events' : matrixConfig;
  let storePath = '';
  if (matrixConfig === 'dynamic_fmhmsm') {
    storePath = '/pages/sport';
  } else if (urlParams.get('searchOption') === 'Events') {
    storePath = '/dynamic?MatrixConfig=Dynamic_HMSM';
  } else {
    storePath = '/dynamic?MatrixConfig=' + matrixConfig;
  }
  const isTicketOnly = matrixConfig === 'dynamic_am';
  const ticketCurrency = sessionStorage.getItem('searchAttractionCurrency') || 'usd';
  useGetPage('Dynamic/Book');

  useEffect(() => {
    if (b2b && pathname !== '/dynamic/book') {
      return;
    }
    const data = sessionStorage.getItem(
      'detailsDynamic' + urlParams.get('matrixConfig') + urlParams.get('searchOption')
    );
    if (data) {
      const parsedData = JSON.parse(data);
      if (parsedData.bookingMode) {
        setPaxes(parsedData.bookingModel.paxes);
      }
      setData(parsedData);
    } else {
      setData({ sessionExpired: true } as DynamicBookResponse);
    }
  }, [matrixConfig, urlParams.get('searchOption')]); // eslint-disable-line

  const onUpdateAddPrice = (newAddPrice: number) => {
    setAddPrice(newAddPrice);

    updateCred(newAddPrice + parseInt(couponAmount || '0'));
  };

  const onClickApply = () => {
    updateCred(parseInt(couponAmount || '0') + addPrice);
  };

  const updateCred = async (value: number) => {
    if (!data?.bookingModel) return;

    let sourcePrice = 0;
    let addPrice = Number(value.toFixed(4));
    let currency = data?.bookingModel?.currency || '';
    if (data.bookingModel.credit2000PaymentInfo) {
      sourcePrice = data.bookingModel.credit2000PaymentInfo?.fullPriceInShekel || 0;
    } else if (data.bookingModel.multiCredit2000PaymentInfo) {
      sourcePrice = data.bookingModel.multiCredit2000PaymentInfo.reduce(
        (acc, p) => acc + p.fullPriceInShekel,
        0
      );
    }
    if (isTicketOnly) {
      currency = ticketCurrency;
      const ticketPaxCount = +(urlParams.get('ticketPaxCount') || 1);
      const ticketPrice = (data.bookingModel.eventInfo.attraction.price.gross as any)[currency];
      addPrice += ticketPrice * (ticketPaxCount - 1);
    }
    const res = await updatePaymentBlock({
      packageCurrency: currency,
      sourcePrice: sourcePrice,
      addPrice: addPrice,
      searchToken: urlParams.get('searchToken') || '',
      lang: i18n.language,
    });

    if (!data?.bookingModel || !res) return;
    setData({
      ...data,
      bookingModel: {
        ...data.bookingModel,
        credit2000PaymentInfo: res.credit2000PaymentInfo,
        multiCredit2000PaymentInfo: res.multiCredit2000PaymentInfo,
      },
    });
  };

  const getBags = () => {
    const bags = data?.bookingModel.flightInfo?.bookFareAndBaggage?.bookFareAndBaggageFlights
      ?.map((fl) => [
        ...(fl.outbound?.listOfCatalogPrice || []),
        ...(fl.inbound?.listOfCatalogPrice || []),
      ])
      .flat();
    const paxesMap = new Map<number, string[]>();
    paxes?.forEach((pax, index) => {
      paxesMap.set(index + 1, [
        ...(pax.baggageOutboundArr || []),
        ...(pax.baggageInboundArr || []),
      ]);
    });
    const result = Array.from(paxesMap.entries()).flatMap(([paxCode, bagIds]) => {
      return bagIds
        .map((id) => {
          const bag = bags?.find((bag) => bag.code === id);
          return bag ? { ...bag, paxCode } : null;
        })
        .filter((bag) => bag !== null);
    });
    return result;
  };
  return (
      <div className="details-wrapper" ref={bookRef}>
        {!!data && !data.hasErrors && data.bookingModel && (
          <div className="flex-column w100p">
            {' '}
            <div className="mb-20 default-shadow flex-between dyn-change-header">
              <Button
                title={t('back')}
                icon={faArrowCircleLeft}
                onClick={() => onClickBack(moduleConfig, 'details')}
                className="btn btn-link"
              />
              <Button
                title={t('search.new')}
                icon={faSearch}
                onClick={() => onClickSearch(moduleConfig, storePath)}
                className="btn btn-link"
              />
            </div>
            <div className="flex-between w100p">
              <DynamicBookForm
                model={data.bookingModel}
                flight={data.bookingModel.flightInfo}
                privilegesInfo={data.privilegesInfo}
                onClickBook={async () => await saveScreenshot(bookRef.current, 'book', 'Dynamic')}
                couponAmount={couponAmount}
                setModel={(model) => setData({ ...data, bookingModel: model })}
                addPrice={addPrice}
                onUpdateAddPrice={onUpdateAddPrice}
                paxes={paxes || []}
                setPaxes={setPaxes}
              />
              <DynamicBookSummary
                model={data.bookingModel}
                couponAmount={couponAmount}
                setCouponAmount={setCouponAmount}
                onClickApply={onClickApply}
                addPrice={addPrice}
                bags={getBags()}
              />
            </div>
          </div>
        )}
        {data?.sessionExpired && <ExpiredToken storePath={storePath} />}
      </div>
  );
};

export default withErrorBoundary(BookDynamicPage, 'BookDynamicPage');
